import React, { forwardRef } from "react";
import { createPortal } from "react-dom";

import Typography from "../Typography";
import { BottomSheetContentWrapper } from "../BottomSheet/BottomSheet.styles";
import CrossIcon from "../../assets/icons/sign/cross.svg";
import COLORS from "../../constants/colors";
import { useViewportSheetDomSync } from "../../hooks/useViewportSheetDomSync";
import { FullViewportBottomSheetProps } from "./FullViewportBottomSheet.types";
import {
    FullViewportSheetCrossIconWrapper,
    FullViewportSheetOverlay,
    FullViewportSheetPanel,
    FullViewportSheetSectionWrapper,
    FullViewportSheetStack,
} from "./FullViewportBottomSheet.styles";

const FullViewportBottomSheet = forwardRef<
    HTMLDivElement,
    FullViewportBottomSheetProps
>((props: FullViewportBottomSheetProps, ref) => {
    const {
        isOpen = false,
        id,
        onToggle,
        children,
        renderTitle,
        titleText,
        titleTextSize = "l",
        renderFooter,
        showCloseIcon = true,
        showHeader = true,
        padded = true,
        paddingBottom,
        backgroundColor = COLORS.WHITE,
        borderRadius = 8,
        showBottomSheetOverlay = true,
        hideBodyScrollbar = false,
        blurBackground = false,
        disableOverlayClick = true,
        bodyStyle,
    } = props;

    useViewportSheetDomSync(isOpen);

    const handleOnClickClose = () => {
        onToggle?.(false);
    };

    const handleOverlayClick = () => {
        if (!disableOverlayClick) {
            onToggle?.(false);
        }
    };

    if (!isOpen || typeof document === "undefined") {
        return null;
    }

    const Header = (
        <>
            {renderTitle ? (
                renderTitle
            ) : (
                <FullViewportSheetSectionWrapper data-popup-header>
                    {titleText ? (
                        <Typography weight="bold" size={titleTextSize}>
                            {titleText}
                        </Typography>
                    ) : null}

                    {showCloseIcon ? (
                        <FullViewportSheetCrossIconWrapper
                            onClick={handleOnClickClose}
                            id={id ? `${id}--closeBtn` : ""}
                        >
                            <CrossIcon fill={COLORS.GREY_900} />
                        </FullViewportSheetCrossIconWrapper>
                    ) : null}
                </FullViewportSheetSectionWrapper>
            )}
        </>
    );

    const content = (
        <FullViewportSheetStack ref={ref} id={id}>
            {showBottomSheetOverlay ? (
                <FullViewportSheetOverlay
                    blurBackground={blurBackground}
                    onClick={handleOverlayClick}
                    aria-hidden
                />
            ) : null}
            <FullViewportSheetPanel borderRadius={borderRadius}>
                <BottomSheetContentWrapper
                    style={{
                        paddingBottom,
                        height: "100%",
                        maxHeight: "100%",
                    }}
                    padded={padded}
                    backgroundColor={backgroundColor}
                    hideBodyScrollbar={hideBodyScrollbar}
                    disableSheetZoom
                >
                    {showHeader ? Header : null}
                    <FullViewportSheetSectionWrapper
                        data-popup-body
                        style={{
                            height: "auto",
                            minHeight: 0,
                            maxHeight: "none",
                            flex: "1 1 auto",
                            ...bodyStyle,
                        }}
                    >
                        {children}
                    </FullViewportSheetSectionWrapper>
                    {renderFooter ? (
                        <FullViewportSheetSectionWrapper data-popup-footer>
                            {renderFooter}
                        </FullViewportSheetSectionWrapper>
                    ) : null}
                </BottomSheetContentWrapper>
            </FullViewportSheetPanel>
        </FullViewportSheetStack>
    );

    return createPortal(content, document.body);
});

export default FullViewportBottomSheet;
